import { useEffect, useState } from 'react'
import { useFetcher } from 'react-router'
import { type PickedLocation, usePickedLocation } from './location'
import type { GeoLocation, loader as geoLoader } from './routes/resource.geo'
import { z } from './zod'

// localStorage key holding a JSON GeoLocation that overrides the CloudFront-
// derived geo. Client-side, so it works against prod too — CloudFront's
// viewer-location headers can't be spoofed from the client (CloudFront
// overwrites them), so this is the supported way to test the site as if
// viewing from elsewhere.
//
// The primary way to set it is the /__geo page (VIT-17678), which ships to
// production and offers presets plus a free-form form. The console one-liner
// remains as a fallback:
//
//   localStorage.setItem('geo-override', JSON.stringify({ city: 'Houston', region: 'TX', country: 'US' }))
//
// then reload; remove with localStorage.removeItem('geo-override').
export const GEO_OVERRIDE_KEY = 'geo-override'

// Omitted fields parse as null (matching "header absent"), so a minimal
// override like {"city":"Houston","country":"US"} is valid.
const GeoOverrideSchema = z.object({
  city: z.string().nullable().default(null),
  region: z.string().nullable().default(null),
  postalCode: z.string().nullable().default(null),
  country: z.string().nullable().default(null),
})

// Malformed JSON or a non-conforming shape yields null (no override) rather
// than throwing — a typo'd override must never break the page.
export const parseGeoOverride = (raw: string | null): GeoLocation | null => {
  if (raw == null) return null
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return null
  }
  const result = GeoOverrideSchema.safeParse(json)
  return result.success ? result.data : null
}

export const readGeoOverride = (): GeoLocation | null => {
  // localStorage.getItem can throw (e.g. storage disabled by browser policy);
  // treat that as "no override".
  try {
    return parseGeoOverride(localStorage.getItem(GEO_OVERRIDE_KEY))
  } catch {
    return null
  }
}

// Trim each free-form field and map '' → null (matching "header absent"), then
// normalise through the same schema the read path uses. Pure — no storage
// access — so the write helpers below stay thin.
export const buildGeoOverride = (fields: Record<'city' | 'region' | 'postalCode' | 'country', string>): GeoLocation =>
  GeoOverrideSchema.parse({
    city: fields.city.trim() === '' ? null : fields.city.trim(),
    region: fields.region.trim() === '' ? null : fields.region.trim(),
    postalCode: fields.postalCode.trim() === '' ? null : fields.postalCode.trim(),
    country: fields.country.trim() === '' ? null : fields.country.trim(),
  })

// localStorage.setItem/removeItem can throw the same way getItem can (storage
// disabled by browser policy, quota); the boolean lets the caller surface the
// failure instead of silently doing nothing.
export const writeGeoOverride = (geo: GeoLocation): boolean => {
  try {
    localStorage.setItem(GEO_OVERRIDE_KEY, JSON.stringify(geo))
    return true
  } catch {
    return false
  }
}

export const clearGeoOverride = (): boolean => {
  try {
    localStorage.removeItem(GEO_OVERRIDE_KEY)
    return true
  } catch {
    return false
  }
}

// A pick made in the location picker, viewed as geo. `country: 'US'` because the
// ZIP dataset behind the typeahead is US-only, which also gives formatGeoLabel's
// US branch ("Portland, OR" rather than a bare city). Pure and exported so the
// mapping is unit-testable in Node.
export const pickedToGeo = (picked: PickedLocation): GeoLocation => ({
  city: picked.city,
  region: picked.region,
  postalCode: picked.postalCode,
  country: 'US',
})

// Shared client-side geo: the localStorage override when present, otherwise a
// fetch of the no-store /resource/geo route after hydration (the landing HTML
// is shared-cached with no header in the cache key, so geo must never be read
// during SSR — see resource.geo.ts).
//
// The transport MUST be a react-router fetcher, not a raw fetch('/resource/geo').
// A fetcher requests the single-fetch URL `/resource/geo.data`, which matches
// the CloudFront `*.data` dynamic behavior — the one carrying the geo
// origin-request policy that injects the CloudFront-Viewer-* headers the
// loader reads. A raw fetch to `/resource/geo` matches no CloudFront behavior
// at all: in production it falls through to the shortener-redirect default
// origin and never reaches the app (broke geo in prod once — VIT-17532), while
// working fine in dev/tests where no CloudFront sits in front.
//
// The fetcher is keyed, so the header, hero, and search form all share one
// fetcher instance and react-router dedupes the load — one /resource/geo.data
// request per page, not one per consumer.
//
// `geo` is undefined until a source resolves; `settled` flips true once one has
// (override found, pick found, or fetch completed — possibly with all-null geo
// when CloudFront headers were absent), letting callers distinguish "still
// loading" from "didn't resolve".
//
// Precedence: geo-override (debug) > the visitor's picked location > CloudFront.
// `picked` is returned alongside `geo` because consumers need more than the
// label — the search form and the results heading build a `zip-#####` URL from
// picked.postalCode, which no GeoLocation field distinguishes from CloudFront's
// IP-derived postal code.
//
// `viewerGeo` is the same resolution WITHOUT the pick — where the visitor
// actually is, rather than where they are searching. Most consumers want `geo`
// (the searched place is what the page is about), but anything describing the
// *person* needs this: the insurance typeahead filters the plan catalog by the
// patient's own state, and a viewer in Oregon searching Texas still carries
// their Oregon plan. The debug override still applies, since its whole purpose
// is to impersonate a viewer elsewhere.
export const useGeo = (): {
  geo: GeoLocation | undefined
  viewerGeo: GeoLocation | undefined
  settled: boolean
  picked: PickedLocation | null
} => {
  const fetcher = useFetcher<typeof geoLoader>({ key: 'geo' })
  const [override, setOverride] = useState<GeoLocation | null>(null)
  const picked = usePickedLocation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetcher identity is stable; one-shot load on mount only.
  useEffect(() => {
    const stored = readGeoOverride()
    if (stored != null) {
      setOverride(stored)
      return
    }
    // Shared keyed fetcher: only kick off the load if another consumer hasn't
    // already (idle + no data yet).
    if (fetcher.state === 'idle' && fetcher.data === undefined) {
      void fetcher.load('/resource/geo')
    }
  }, [])

  // Where the visitor is, independent of anywhere they have chosen to search.
  const viewerGeo = override ?? fetcher.data?.geo

  if (override != null) {
    return { geo: override, viewerGeo, settled: true, picked }
  }
  // An explicit pick beats CloudFront's IP guess — that is the whole point of
  // the picker. A GPS-only pick carries no city or ZIP, so it falls through to
  // the CloudFront label rather than blanking it (coords don't resolve to a name
  // yet — see location.ts).
  if (picked != null && (picked.city != null || picked.postalCode != null)) {
    return { geo: pickedToGeo(picked), viewerGeo, settled: true, picked }
  }
  return {
    geo: fetcher.data?.geo,
    viewerGeo,
    settled: fetcher.state === 'idle' && fetcher.data !== undefined,
    picked,
  }
}
